import React, { useState } from 'react';
import { Calendar, Loader, Activity, Info } from 'lucide-react';
import { ServiceRow } from '../google/ServiceRow';
import { MicrosoftAccountInfo, MicrosoftCalendar } from './useMicrosoftAccount';

interface OutlookCalendarRowProps {
  active: boolean;
  connected: boolean;
  expanded: boolean;
  accountInfo: MicrosoftAccountInfo;
  calendars: MicrosoftCalendar[];
  fetchingCalendars: boolean;
  onToggle: () => void;
  onActivate: () => void;
  onSave: (updates: {
    microsoft_sync_activities?: boolean;
    microsoft_sync_activities_calendar_id?: string;
    microsoft_calendar_ids?: string[];
  }) => Promise<boolean>;
}

export function OutlookCalendarRow({
  active,
  connected,
  expanded,
  accountInfo,
  calendars,
  fetchingCalendars,
  onToggle,
  onActivate,
  onSave,
}: OutlookCalendarRowProps) {
  const [saving, setSaving] = useState(false);
  const selectedCalendars = accountInfo.microsoft_calendar_ids;

  const handleToggleCalendar = async (calendarId: string) => {
    const next = selectedCalendars.includes(calendarId)
      ? selectedCalendars.filter(id => id !== calendarId)
      : [...selectedCalendars, calendarId];
    setSaving(true);
    await onSave({ microsoft_calendar_ids: next });
    setSaving(false);
  };

  const handleToggleSyncActivities = async (value: boolean) => {
    setSaving(true);
    await onSave({ microsoft_sync_activities: value });
    setSaving(false);
  };

  const handleSelectActivityCalendar = async (calendarId: string) => {
    setSaving(true);
    await onSave({ microsoft_sync_activities_calendar_id: calendarId });
    setSaving(false);
  };

  return (
    <ServiceRow
      icon={Calendar}
      title="Calendario Outlook"
      description="Sincronizza eventi e attività con il calendario Outlook"
      active={active}
      connected={connected}
      expanded={expanded}
      saving={saving}
      providerName="Microsoft"
      onToggle={onToggle}
      onActivate={onActivate}
    >
      <div className="space-y-4 py-2">
        {/* Lista calendari */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-[#37352F] dark:text-white">Calendari sincronizzati</p>
            {fetchingCalendars && <Loader className="h-3 w-3 animate-spin text-[#787774]" />}
          </div>
          {calendars.length === 0 && !fetchingCalendars ? (
            <p className="text-xs text-[#787774] dark:text-white/40">Nessun calendario disponibile</p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {calendars.map(cal => (
                <label
                  key={cal.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedCalendars.includes(cal.id)}
                    onChange={() => handleToggleCalendar(cal.id)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#0078d4] focus:ring-[#0078d4]"
                  />
                  <span className="text-xs text-[#37352F] dark:text-white truncate">{cal.name}</span>
                  {cal.isDefault && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-[#0078d4]/10 text-[#0078d4] rounded uppercase font-medium">
                      Predefinito
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Sync attività */}
        <div className="pt-3 border-t border-[rgba(55,53,47,0.06)] dark:border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Activity className="h-3.5 w-3.5 text-[#787774]" />
              <span className="text-xs font-medium text-[#37352F] dark:text-white">
                Sincronizza le attività sul calendario
              </span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={accountInfo.microsoft_sync_activities}
                onChange={(e) => handleToggleSyncActivities(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-[#0078d4] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all" />
            </label>
          </div>
          {accountInfo.microsoft_sync_activities && (
            <div className="mt-2">
              <label className="block text-xs text-[#787774] dark:text-white/50 mb-1">
                Calendario destinazione attività
              </label>
              <select
                value={accountInfo.microsoft_sync_activities_calendar_id || ''}
                onChange={(e) => handleSelectActivityCalendar(e.target.value)}
                className="w-full text-xs px-2 py-1.5 rounded-md border border-[rgba(55,53,47,0.12)] dark:border-white/10 bg-white dark:bg-[#191919] text-[#37352F] dark:text-gray-100 focus:outline-none focus:border-[#0078d4]"
              >
                <option value="">— Seleziona —</option>
                {calendars.map(cal => (
                  <option key={cal.id} value={cal.id}>{cal.name}</option>
                ))}
              </select>
            </div>
          )}

          {accountInfo.microsoft_sync_activities && accountInfo.google_sync_activities && (
            <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30">
              <Info className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800 dark:text-blue-300">
                Hai attivo anche il push su Google Calendar: le attività verranno create su entrambi i calendari.
              </p>
            </div>
          )}
        </div>
      </div>
    </ServiceRow>
  );
}
